"use client";

import React, { useState, useEffect, useRef } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import MenuLogo from "./megamenu/MenuLogo";
import MenuItem from "./megamenu/MenuItem";
import SubmenuPanel from "./megamenu/SubmenuPanel";
import ContactButton from "./megamenu/ContactButton";
import HamburgerButton from "./megamenu/HamburgerButton";
import MobileCallToAction from "./megamenu/MobileCallToAction";
import SubmenuPanelV2 from "./megamenu/SubmenuPanelV2";
import Image from "next/image";

interface MenuProps {
  Hover: boolean;
  Scrolled: boolean;
  setHovered: (v: boolean) => void; // NEW: notify header hover state
  onMouseLeave?: () => void;
  data: any;
  setActiveMenu: (label: string | null) => void;
  setActiveMenuLink: (link: string | null) => void;
  activeMenuLabel: string | null;
  activeMenuLink: string | null;
  setMenuToggle: (flag: boolean) => void;
}

const MegaMenu: React.FC<MenuProps> = ({
  Hover,
  Scrolled,
  setHovered,
  data,
  setActiveMenu,
  setActiveMenuLink,
  activeMenuLabel,
  activeMenuLink,
  setMenuToggle,
}) => {
  const menuItems = data?.data?.attributes?.menu ?? [];

  const [activeMain, setActiveMain] = useState<string | null>(
    menuItems.length > 0 ? menuItems[0]?.id : null,
  );
  const [activeSub, setActiveSub] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isWideScreen, setIsWideScreen] = useState(false);
  // "open" when a panel appears from closed (full reveal), "swap" when moving
  // between titles while one is already showing (content fade only).
  const [panelMotion, setPanelMotion] = useState<"open" | "swap">("open");

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isTouch = useIsTouchDevice();
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

  // Detect wide screen. Matches the `lg` breakpoint, where the nav switches
  // from the mobile full-screen menu to the mega-menu panel, so tablets size
  // the panel to its content and scroll instead of forcing full height.
  useEffect(() => {
    const handleResize = () => setIsWideScreen(window.innerWidth >= 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setMenuToggle(isOpen);
  }, [isOpen, setMenuToggle]);

  // Lock scroll when menu open
  useEffect(() => {
    const root = document.documentElement;
    if (activeMain || isOpen) {
      // Hiding the scrollbar widens the page and shifts centred content.
      // Record its width before it goes (only on the unlocked -> locked step;
      // once hidden it measures 0) so body, header and panel can pad it back.
      if (document.body.style.overflow !== "hidden") {
        root.style.setProperty(
          "--menu-scrollbar-gap",
          `${window.innerWidth - root.clientWidth}px`,
        );
      }
      document.body.classList.add("overflow-y-hidden-no");
      setHovered(true); // header should stay white
    } else {
      document.body.classList.remove("overflow-y-hidden-no");
      root.style.removeProperty("--menu-scrollbar-gap");
      setHovered(false); // reset header hover if no menu open
    }
    if (!!activeMain || isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "auto";
  }, [activeMain, isOpen, setHovered]);

  // Clear active menu on home page
  useEffect(() => {
    if (
      (pathname === "/" || pathname === "") &&
      (activeMenuLabel || activeMenuLink)
    ) {
      setActiveMenu(null);
      setActiveMenuLink(null);
    }
  }, [
    pathname,
    activeMenuLabel,
    activeMenuLink,
    setActiveMenu,
    setActiveMenuLink,
  ]);

  const toggleMenu = (link?: string) => {
    setIsOpen(!isOpen);
    setActiveMain(null);
    setActiveSub(null);
    setHovered(false);
    if (!!link) router.push(link);
  };

  const closeMenu = (link?: string) => {
    setIsOpen(false);
    setActiveMain(null);
    setActiveSub(null);
    setHovered(false);
    if (!!link) router.push(link);
  };

  // Crossfade on switch: the outgoing panel stays mounted on top for the
  // length of its fade-out (see .mega-panel-leave) while the new one renders
  // underneath, so the panel is never momentarily empty. It must be set in the
  // same update as activeMain; an effect would leave a one-frame gap.
  const [leavingMain, setLeavingMain] = useState<string | null>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeMainRef = useRef(activeMain);
  activeMainRef.current = activeMain;

  const crossfadeFrom = (fromId: string | null) => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    setLeavingMain(fromId);
    leaveTimer.current = setTimeout(() => {
      leaveTimer.current = null;
      setLeavingMain(null);
    }, 320);
  };

  useEffect(() => {
    if (activeMain) return;
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    leaveTimer.current = null;
    setLeavingMain(null);
  }, [activeMain]);

  useEffect(
    () => () => {
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    },
    [],
  );

  const toggleMainMenu = (
    id: string | null,
    isWithOutSubMenu: boolean = false,
  ) => {
    if (isWithOutSubMenu) {
      setIsOpen(!isOpen);
      setActiveMain(null);
      setActiveSub(null);
      setHovered(false); // Reset hovered state when navigating
    } else {
      if (id !== null && id !== activeMain) {
        const panelShowing = !!menuItems.find(
          (menu: any) => menu?.id === activeMain,
        )?.submenu?.length;
        setPanelMotion(panelShowing ? "swap" : "open");
        if (panelShowing) crossfadeFrom(activeMain);
      }
      setActiveMain(activeMain === id ? null : id);
      setActiveSub(null);
    }
  };

  // Hover intent: while a panel is open, switching to another title happens
  // 100ms after the pointer settles on it; any movement over 10px restarts
  // the countdown. Sweeping across the nav (e.g. Services to Contact us) so
  // never flashes the panels on the way. A plain delay wasn't enough: the
  // pointer can spend 100ms+ crossing a wide title mid-sweep. Opening from
  // closed stays instant.
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSwitch = useRef<{ id: string; x: number; y: number } | null>(
    null,
  );
  const pointer = useRef({ x: 0, y: 0 });

  const cancelHoverSwitch = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    pendingSwitch.current = null;
  };

  const startSwitchCountdown = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      const target = pendingSwitch.current;
      hoverTimer.current = null;
      pendingSwitch.current = null;
      if (!target) return;
      // Ref, not the closure: activeMain may have moved on since the
      // countdown started.
      crossfadeFrom(activeMainRef.current);
      setPanelMotion("swap");
      setActiveMain(target.id);
      setActiveSub(null);
    }, 100);
  };

  const handlePointerMove = (e: React.MouseEvent) => {
    pointer.current = { x: e.clientX, y: e.clientY };
    const pending = pendingSwitch.current;
    if (
      pending &&
      Math.hypot(e.clientX - pending.x, e.clientY - pending.y) > 10
    ) {
      pendingSwitch.current = { ...pending, x: e.clientX, y: e.clientY };
      startSwitchCountdown();
    }
  };

  const hoverMainMenu = (id: string) => {
    cancelHoverSwitch();
    // Back on the title that is already open: just drop any pending switch.
    if (id === activeMain) return;

    const panelShowing = !!menuItems.find(
      (menu: any) => menu?.id === activeMain,
    )?.submenu?.length;
    if (!panelShowing) {
      toggleMainMenu(id);
      return;
    }

    pendingSwitch.current = { id, ...pointer.current };
    startSwitchCountdown();
  };

  useEffect(() => cancelHoverSwitch, []);

  // Close every menu layer (desktop panel and mobile drawer) when the route
  // changes, however the navigation was triggered.
  useEffect(() => {
    cancelHoverSwitch();
    setHovered(false);
    setActiveMain(null);
    setActiveSub(null);
    setIsOpen(false);
  }, [pathname, searchParams, setHovered]);

  const toggleSubMenu = (id: string | null) => {
    setActiveSub(activeSub === id ? null : id);
  };

  const handleLogoClick = () => {
    setActiveMenu(null);
    setActiveMenuLink(null);
    closeMenu();
  };

  const handleContactClick = () => {
    setActiveMenu(null);
    setActiveMenuLink(null);
    closeMenu();
  };

  const getActiveMenuData = () => {
    return data?.data?.attributes?.menu?.find(
      (item: any) => item?.id === activeMain,
    );
  };

  const logoUrl = data?.data?.attributes?.logo?.data?.attributes?.url
    ? `${baseUrl}${data.data.attributes.logo.data.attributes.url}`
    : "/images/logo.svg";

  return (
    <>
      <div
        className="w-full mx-auto relative z-50 py-5 xl:py-[20px] h-[80px]"
        onMouseMove={handlePointerMove}
        onMouseLeave={() => {
          cancelHoverSwitch();
          toggleMainMenu(null);
        }}
      >
        <div className="xxl:max-w-[1536px] xl:max-w-[1280px] lg:max-w-[1280px] container-header w-full mx-auto lg:px-[24px] px-[24px] flex items-center justify-between">
          {/* Logo */}
          <MenuLogo logoUrl={logoUrl} onLogoClick={handleLogoClick} />

          {/* Navigation */}
          <nav
            data-lenis-prevent={isOpen || undefined}
            className={`${isOpen ? "mobile-fullscreen border-t-1 border-t-blue-200 lg:bg-transparent !bg-[#000000b5] " : "hidden"} lg:flex lg:px-0 xl:px-[10px] lg:relative`}
            style={
              isOpen
                ? {
                  top: "calc(80px + var(--df-banner-offset, 0px))",
                  height:
                    "calc(100dvh - 80px - var(--df-banner-offset, 0px))",
                }
                : undefined
            }
          >
            <div className="lg:p-0 px-[24px] lg:min-h-auto h-auto min-h-[60dvh] py-[24px_37px] xl:space-x-[32px] lg:space-x-[16px] lg:relative w-full lg:flex lg:bg-transparent bg-white relative z-[2] lg:after:content-none">
              <div className="lg:hidden block absolute bottom-0 right-0 w-full h-[40%] z-0">
                <Image
                  src={"/images/mega-menu-bg-mobile.png"}
                  alt=""
                  width={200}
                  height={200}
                  className="w-full h-full"
                />
              </div>
              {data?.data?.attributes?.menu?.map((item: any, i: number) => (
                <React.Fragment key={item.id}>
                  <MenuItem
                    item={item}
                    isScrolled={Scrolled}
                    isHover={Hover}
                    activeMain={activeMain}
                    isTouch={isTouch}
                    onToggleMainMenu={toggleMainMenu}
                    onHoverMainMenu={hoverMainMenu}
                    onHoverEnd={cancelHoverSwitch}
                    onSetActiveMenu={setActiveMenu}
                  />

                  {/* <SubmenuPanel
                  item={item}
                  activeMain={activeMain}
                  activeSub={activeSub}
                  activeMenuLink={activeMenuLink}
                  isWideScreen={isWideScreen}
                  baseUrl={baseUrl}
                  onToggleMainMenu={toggleMainMenu}
                  onToggleSubMenu={toggleSubMenu}
                  onToggleMenu={closeMenu}
                  onSetActiveMenuLink={setActiveMenuLink}
                  onSetActiveMenu={setActiveMenu}
                /> */}
                  <SubmenuPanelV2
                    item={item}
                    index={i}
                    motion={panelMotion}
                    isLeaving={
                      leavingMain === item.id && activeMain !== item.id
                    }
                    activeMain={activeMain}
                    activeSub={activeSub}
                    activeMenuLink={activeMenuLink}
                    isWideScreen={isWideScreen}
                    baseUrl={baseUrl}
                    onToggleMainMenu={toggleMainMenu}
                    onToggleSubMenu={toggleSubMenu}
                    onToggleMenu={closeMenu}
                    onSetActiveMenuLink={setActiveMenuLink}
                    onSetActiveMenu={setActiveMenu}
                  />
                </React.Fragment>
              ))}
            <MobileCallToAction onContactClick={handleContactClick} />
            </div>

          </nav>

          <ContactButton onContactClick={handleContactClick} />

          <HamburgerButton
            isOpen={isOpen}
            isScrolled={Scrolled || Hover}
            onClick={() => toggleMenu()}
          />
        </div>
      </div>

      {/* Overlay */}
      {activeMain && !!getActiveMenuData()?.submenu?.length && (
        <div
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          className="mega-overlay fixed inset-0 backdrop-blur-sm bg-[#010101E5] z-40 hidden lg:block"
          style={{
            top: "calc(80px + var(--df-banner-offset, 0px))",
            height: "calc(100vh - 80px - var(--df-banner-offset, 0px))",
          }}
        />
      )}
    </>
  );
};

export default MegaMenu;
